import { AuditTimeline } from '@/features/audit/audit-timeline'
import { useAuditLogQuery } from '@/features/audit/hooks/use-audit-log'
import { useUsersLookupQuery } from '@/features/users/hooks/use-users-query'
import { Skeleton } from '@/shared/ui/skeleton'

interface ClientHistoryTabProps {
  /**
   * `cli_` del cliente. La bitácora sella el `entityId` con el id PÚBLICO, en v1 y en v2: hasta
   * el 2026-07-31 v1 sellaba el `_id` y v2 el `cli_`, y esa asimetría partía el historial de un
   * mismo cliente en dos espacios que la lectura —igualdad exacta, sin puente— no reconcilia.
   */
  clientId: string
}

/**
 * "Historial" tab: la bitácora de la entidad CLIENTE (quién la creó / modificó / eliminó),
 * leída del log central (`/audit`, entityType `client`). Mismo patrón que el historial del
 * expediente del portal; el actor interno se resuelve por su `_id` de Mongo.
 */
export function ClientHistoryTab({ clientId }: ClientHistoryTabProps) {
  const { data: users = [] } = useUsersLookupQuery()
  const { data, isLoading, isError } = useAuditLogQuery('client', clientId)

  const usersById = new Map(users.map((u) => [u._id, u.fullname]))
  const resolveActor = (id: string) => usersById.get(id) ?? 'Equipo interno'

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (isError) {
    return <p className="text-sm text-destructive">No se pudo cargar el historial.</p>
  }

  if (!data || data.items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Este cliente todavía no tiene actividad registrada.
      </p>
    )
  }

  return <AuditTimeline entries={data.items} resolveActor={resolveActor} />
}
